import { showNotification } from "@/plugins/notification";

import router from "@/router/index.js";
import { getUserFriendlyErrorMessage } from '@/helpers/errorHandlerHelper.js';
import { triggerSessionExpired } from '@/services/errorServices/sessionExpired';
import { trackAnalyticsEvent } from '@/services/firebase';

// GA4 brand dimension — see docs/GOOGLE_ANALYTICS_TRACKING_PLAN.md.
const analyticsBrand = () => import.meta.env.VITE_THEME_PRESET;

/**
 * Vuex actions for the "userauth" module.
 *
 * Implements the app's authentication flows: fetching/refreshing the current
 * user (getMe), phone+password signup/login (signUp/logIn), phone-SMS
 * verification signup/login (sendSmsCode/verifySmsCode) with optional 2FA
 * (verifyTwoFaLogin), "Sign In With Farcaster" callback handling
 * (siwfCallback), X (Twitter) account linking (logInWithX/fetchDisconnectX/
 * authWithX), logout, and account deletion. Most actions call the REST API
 * directly via `fetch`, mirror the fetched profile into both Vuex
 * (`setUserData`) and `localStorage` ("user") so it survives reloads, and
 * backfill a default `weighInThresholds` array when the API omits it.
 */
export default {
  /** Fetch the current user's profile from `/api/users/me`; on 401/403 clears the stored session, otherwise persists and commits the profile. Returns the profile, or `null` on failure/no-op. */
  async getMe({ commit }) {
    if (!commit) return;

    try {
      let response = await fetch(`/api/users/me`);

      if (response.status === 403 || response.status === 401) {
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.warn('Failed to parse error response:', jsonError);
          data = {};
        }

        if (
          data?.message === "User account is deleted" ||
          data?.message === "User no longer exists"
        ) {
          triggerSessionExpired();
          return;
        }

        // For other 401/403 errors, don't try to parse response again
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
        return null;
      }

      // Only parse response if it's not an error status
      let profile;
      try {
        profile = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse user profile response:', jsonError);
        throw new Error('Invalid response format');
      }

      if (!profile.weighInThresholds?.[0]) {
        let arr = [1, 2, 3, 4, 5];
        arr.forEach((item) => {
          profile.weighInThresholds?.push({ threshold: "off" });
        });
      }

      if (profile.statusCode === 401 || profile.statusCode === 403) {
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
      } else {
        localStorage.setItem("user", JSON.stringify(profile));
        commit("setUserData", profile);
        commit("setAuthStatus", true);
      }
      return profile;
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      console.error('Authentication error:', friendlyMessage);

      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original auth error:', err);
      }
      commit("setAuthStatus", false);
      return null;
    }
  },
  /** Register a new account via phone+password, then load and persist its profile and navigate home on success. Returns `{ status, message, data }`. */
  async signUp({ commit }, { phone, password }) {
    try {
      const response = await fetch(`/api/users/signup`, {
        method: "POST",
        body: JSON.stringify({
          phone,
          password,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        const message = data?.message || "Sign up failed. Please try again.";
        commit("setUserDataError", { ...data, message });
        return { status: "error", message, data };
      }

      if (!data.id || data.id === "null") {
        const message = "User ID is required";
        console.error(message, new Error().stack);
        commit("setUserDataError", { message });
        return { status: "error", message, data };
      }

      const profileResponse = await fetch(`/api/users/${data.id}`);
      const profile = await profileResponse.json();

      if (!profileResponse.ok) {
        const message = profile?.message || "Could not load user profile.";
        commit("setUserDataError", { ...profile, message });
        return { status: "error", message, data: profile };
      }

      localStorage.setItem("user", JSON.stringify(profile));
      commit("setUserData", profile);
      commit("setAuthStatus", true);
      router.push("/");
      return { status: "success", data: profile };
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      commit("setUserDataError", { message: friendlyMessage });

      if (import.meta.env.MODE === 'development') {
        console.error('Original signup error:', error);
      }

      return { status: "error", message: friendlyMessage };
    }
  },
  /** Log in via phone+password, then load/persist the profile (backfilling default `weighInThresholds`) and navigate home. Returns `{ status, message, data }`. */
  async logIn({ commit, dispatch }, { phone, password }) {
    try {
      const response = await fetch(`/api/auth/login`, {
        method: "POST",
        body: JSON.stringify({
          phone,
          password,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        const message = data?.message || "Login failed. Please try again.";
        commit("setUserDataError", { ...data, message });
        return { status: "error", message, data };
      }

      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          const message = "User ID is required";
          console.error(message, new Error().stack);
          commit("setUserDataError", { message });
          router.push("/onboarding/login-method");
          return { status: "error", message, data };
        }

        const profileResponse = await fetch(`/api/users/${data.id}`);
        const profile = await profileResponse.json();

        if (!profileResponse.ok) {
          const message = profile?.message || "Could not load user profile.";
          commit("setUserDataError", { ...profile, message });
          return { status: "error", message, data: profile };
        }

        if (!profile.weighInThresholds?.[0]) {
          profile.weighInThresholds = Array.isArray(profile.weighInThresholds) ? profile.weighInThresholds : [];
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        commit("setUserData", profile);
        commit("setAuthStatus", true);
        trackAnalyticsEvent("login", { method: "password", user_id_set: true, brand: analyticsBrand() });
        // Refresh from /api/users/me now that the session is authenticated,
        // so the store holds the canonical "me" shape (not the by-id profile above).
        dispatch("getMe").catch(() => {});
        router.push("/");
        return { status: "success", data: profile };
      } else {
        const message = data?.message || "Login failed. Please try again.";
        commit("setUserDataError", { ...data, message });
        return { status: "error", message, data };
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      commit("setUserDataError", { message: friendlyMessage });

      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original error:', error);
      }

      return { status: "error", message: friendlyMessage };
    }
  },
  /**
   * Log out. Fires the server logout and push-token unregister as
   * best-effort, fire-and-forget calls (`keepalive: true`, errors swallowed)
   * so a slow/failing API never blocks the local logout, then unconditionally
   * clears local auth state/localStorage and hard-redirects to `/sign-in`.
   */
  async logOut({ commit }, error) {
    // OLD: local session was only cleared when the logout API replied ok/403.
    // The FastAPI backend can answer 401 ({"status_id": 0, "reason": "Token is
    // missing"}) or fail entirely — logOut then did NOTHING: the dead session
    // stayed in localStorage and every leftbar click bounced back via the
    // useAPI 401 handler, making the side menu appear unresponsive.
    // OLD code:
    // let response = await fetch(`/api/auth/logout`, { ... });
    // if (response.ok || response.status === 403) { ...clear + redirect... }
    // FIX: best-effort server logout, but ALWAYS clear local state and redirect.
    // OLD (previous fix): await fetch(`/api/auth/logout`, ...) — the FastAPI
    // logout call takes ~1s+ and the redirect waited on it, so clicking
    // "Log out" felt slow. Fire-and-forget instead: keepalive lets the
    // request complete even after window.location.replace unloads the page.
    try {
      fetch(`/api/auth/logout`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
      }).catch(() => {
        // network/API failure must not block local logout
      });
    } catch {
      // network/API failure must not block local logout
    }
    // Fire sign_out BEFORE local user data is cleared — trackAnalyticsEvent
    // reads the current user id off the store to attribute the event, so it
    // must run while that id is still present.
    let hadUserId = false;
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      hadUserId = !!storedUser?.id;
    } catch {
      hadUserId = false;
    }
    trackAnalyticsEvent("sign_out", {
      method: "manual",
      session_had_user_id: hadUserId,
      brand: analyticsBrand(),
    });
    commit("setAuthStatus", false);
    let data = null;
    localStorage.removeItem("activeTab");
    localStorage.removeItem("user");
    localStorage.removeItem("isFirstLogin");
    commit("setProfile", data);
    commit("setUserData", data);
    // router.push("/welcome");
    //  router.push("/sign-in");
    window.location.replace("/sign-in");
  },

  /** Request an SMS login/signup code for `phone` (with captcha token and optional referral code/email); on success flips `state.showSmsCodeInput` so the UI shows the code-entry step. Returns `{ status, message }`. */
  async sendSmsCode({ commit }, { phone, captchaToken, type, referralCode, email }) {
    try {
      const body = { phone, type };
      if (referralCode) body.referralCode = referralCode;
      if (email) body.email = email;
      const response = await fetch(`/api/auth/login-sms/send`, {
        method: "POST",
        body: JSON.stringify(body),
        credentials: "include",
        referrer: window.location.origin,
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken || "",
        },
      });
      const data = await response.json();
      if (!response.ok || data.status_id === 0) {
        const message = data.reason || data.message || 'Something went wrong';
        commit("setUserDataError", message);
        return { status: "error", message };
      }
      if (response.status === 200) {
        commit("setShowSmsCodeInput", true);
        return {
          message: "Success",
          status: "success",
        };
      } else {
        const data = await response.json();
        // console.log("error", data.message);
        return {
          message: data.message,
          status: "error",
        };
      }
    } catch (error) {
      // console.log("error", error);
      return {
        message: "Server error",
        status: "error",
      };
    }
  },
  /**
   * Verify an SMS code (optionally with signup details: username, name,
   * email, referral code, address). On success loads and persists the
   * profile (backfilling default `weighInThresholds`) and returns
   * `{ status: "success", profile }`; if the account requires 2FA, returns
   * `{ status: "twofa", tempToken, userId }` instead without persisting
   * anything, so the caller can route to the 2FA step.
   */
  async verifySmsCode(
    { commit },
    {
      phone,
      code,
      username,
      firstName,
      lastName,
      email,
      referralCode,
      county,
      streetAddress,
      streetAddress2,
      city,
      state,
      zipCode,
    }
  ) {
    try {
      const address = {
        street1: streetAddress || "",
        street2: streetAddress2 || "",
        city: city || "",
        state: state || "",
        zipcode: zipCode || "",
      };
      const requestData = {
        phone,
        code,
         // OLD: ...(username && { username }),
        // FIX: trim as a backstop — a username registered with trailing spaces can't be found later
        ...(username && { username: username.trim() }),
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(email && { email }),
        ...(referralCode && { referralCode }),
        ...(county && { county }),
        ...(Object.keys(address).length && { address }),
      };
      const response = await fetch(`/api/auth/login-sms/check`, {
        method: "POST",
        body: JSON.stringify(requestData),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || data.status_id === 0) {
        const message = data.reason || data.message || 'Something went wrong';
        commit("setUserDataError", message);
        return { status: "error", message };
      }

      if (data?.twoFaRequired) {
        return {
          status: "twofa",
          tempToken: data.tempToken,
          userId: data.userId,
          message: data.message,
        };
      }
      if (data.statusCode === 409) {
        commit("setUserDataError", data.message);
        return {
          status: "error",
          usernameTaken: true,
          message: data.message,
        };
      }
      if (data.error) {
        commit("setUserDataError", data.message);
        return {
          status: "error",
          message: data.message,
        };
      }
      const isSuccess = data.status_id === 1 || data.statusCode == 200 || (!data.statusCode && !data.error && !data.reason);
      if (isSuccess) {
        const userId = data.user?.id || data.id;
        if (!userId || userId === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
        }
        let response = await fetch(`/api/users/${userId}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        localStorage.removeItem("userPhoneX");
        commit("setUserData", profile);
        commit("setAuthStatus", true);
        trackAnalyticsEvent("login", { method: "sms_otp", user_id_set: true, brand: analyticsBrand() });
        return {
          status: "success",
          message: "",
          profile,
        };
      } else {
        commit("setUserDataError", data);
        return {
          status: "error",
          message: data.message,
        };
      }
    } catch (error) {
      // console.log("error", error);
      commit("setUserDataError", error.message);
      return {
        status: "error",
        message: error.message,
      };
    }
  },
  /**
   * Request an email login code for `email` (with captcha token and optional referral code).
   * There is no dedicated email-channel endpoint — this hits the same
   * `/api/auth/login-sms/send` endpoint as `sendSmsCode`, just with an
   * `email` field in the payload instead of `phone`.
   * Returns `{ status, message }`.
   */
  async sendEmailCode({ commit }, { email, captchaToken, type, referralCode }) {
    try {
      const body = { email, type };
      if (referralCode) body.referralCode = referralCode;
      const response = await fetch(`/api/auth/login-sms/send`, {
        method: "POST",
        body: JSON.stringify(body),
        credentials: "include",
        referrer: window.location.origin,
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken || "",
        },
      });
      const data = await response.json();
      if (!response.ok || data.status_id === 0) {
        const message = data.reason || data.message || 'Something went wrong';
        commit("setUserDataError", message);
        return { status: "error", message };
      }
      if (response.status === 200) {
        commit("setShowSmsCodeInput", true);
        return {
          message: "Success",
          status: "success",
        };
      } else {
        const data = await response.json();
        return {
          message: data.message,
          status: "error",
        };
      }
    } catch (error) {
      return {
        message: "Server error",
        status: "error",
      };
    }
  },
  /**
   * Verify an email login code. There is no dedicated email-channel endpoint —
   * this hits the same `/api/auth/login-sms/check` endpoint as `verifySmsCode`,
   * just with an `email` field in the payload instead of `phone`.
   * On success loads and persists the profile (backfilling default
   * `weighInThresholds`) and returns `{ status: "success", profile }`; if the
   * account requires 2FA, returns `{ status: "twofa", tempToken, userId }` instead.
   */
  async verifyEmailCode({ commit }, { email, code }) {
    try {
      const requestData = { email, code };
      const response = await fetch(`/api/auth/login-sms/check`, {
        method: "POST",
        body: JSON.stringify(requestData),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || data.status_id === 0) {
        const message = data.reason || data.message || 'Something went wrong';
        commit("setUserDataError", message);
        return { status: "error", message };
      }

      if (data?.twoFaRequired) {
        return {
          status: "twofa",
          tempToken: data.tempToken,
          userId: data.userId,
          message: data.message,
        };
      }
      if (data.error) {
        commit("setUserDataError", data.message);
        return {
          status: "error",
          message: data.message,
        };
      }
      const isSuccess = data.status_id === 1 || data.statusCode == 200 || (!data.statusCode && !data.error && !data.reason);
      if (isSuccess) {
        const userId = data.user?.id || data.id;
        if (!userId || userId === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
        }
        let response = await fetch(`/api/users/${userId}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        localStorage.removeItem("userPhoneX");
        commit("setUserData", profile);
        commit("setAuthStatus", true);
        trackAnalyticsEvent("login", { method: "email_otp", user_id_set: true, brand: analyticsBrand() });
        return {
          status: "success",
          message: "",
          profile,
        };
      } else {
        commit("setUserDataError", data);
        return {
          status: "error",
          message: data.message,
        };
      }
    } catch (error) {
      commit("setUserDataError", error.message);
      return {
        status: "error",
        message: error.message,
      };
    }
  },

  //api for two facto...
  // ✅ VERIFY 2FA LOGIN
/** Complete a 2FA login by verifying `code` for `userId`; on success loads/persists the profile (backfilling default `weighInThresholds`) and returns `{ status: "success", profile }`. */
async verifyTwoFaLogin({ commit }, { userId, code }) {
  try {
    const requestData = {
      userId,
      code,
    };

    const response = await fetch(`/api/auth/2fa/verify-login`, {
      method: "POST",
      body: JSON.stringify(requestData),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // allows backend cookie (JWT) to be set
    });

    const data = await response.json();

    // Invalid 2FA code
    if (!response.ok || data.error) {
      commit("setUserDataError", data.message || "Invalid 2FA code");
      return {
        status: "error",
        message: data.message || "Invalid 2FA code",
      };
    }

    // Successful 2FA verification → get user profile
    if (data.id) {
      const userRes = await fetch(`/api/users/${data.id}`);
      const profile = await userRes.json();

      // Set defaults like your existing login
      if (!profile.weighInThresholds[0]) {
        let arr = [1, 2, 3, 4, 5];
        arr.forEach((item) => {
          profile.weighInThresholds.push({ threshold: "off" });
        });
      }

      // Save in localStorage and Vuex
      localStorage.setItem("user", JSON.stringify(profile));
      console.error(" JSON.stringify(profile)", JSON.stringify(profile));
      commit("setUserData", profile);
      commit("setAuthStatus", true);
      // This is where login actually completes when 2FA is required — the
      // earlier verifySmsCode() success branch returns "twofa" instead of
      // "success" in that case, so `login` is not fired there.
      trackAnalyticsEvent("login", { method: "sms_otp", user_id_set: true, brand: analyticsBrand() });

      return {
        status: "success",
        message: "2FA verified successfully",
        profile,
      };
    } else {
      return {
        status: "error",
        message: data.message || "Something went wrong",
      };
    }
  } catch (error) {
    console.error("2FA verification failed:", error);
    commit("setUserDataError", error.message);
    return {
      status: "error",
      message: error.message,
    };
  }
},

  /** Complete the X (Twitter) OAuth link using the returned token/verifier; on success clears the stored oauth params, marks `isPostToX`, refreshes the profile, and shows a success notification. */
  async logInWithX({ commit }, { oauthToken, oauthVerifier }) {
    try {
      let response = await fetch(`/api/users/connect-x`, {
        method: "POST",
        body: JSON.stringify({
          oauthToken,
          oauthVerifier,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) {
        // console.log("error", data.message);
        localStorage.removeItem("oauthToken");
        localStorage.removeItem("oauthVerifier");
  localStorage.setItem("isPostToX", String(false));
        commit("setUserDataError", data.message);
        return {
          status: "error",
          message: data.message,
        };
      }
      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        let response = await fetch(`/api/users/${data.id}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        localStorage.removeItem("oauthToken");
        localStorage.removeItem("oauthVerifier");
  localStorage.setItem("isPostToX", String(true));
        router.push("/");
        showNotification(
          "success",
          `X Account Connected!`,
          "Your X account has been successfully connected. Your posts will now be automatically published to X.",
          7000
        );
        commit("setUserData", profile);
        return {
          status: "success",
          message: "",
        };
      } else {
        commit("setUserDataError", data);
        return {
          status: "error",
          message: data.message,
        };
      }
    } catch (error) {
      // console.log("error", error);
      commit("setUserDataError", error.message);
      return {
        status: "error",
        message: error.message,
      };
    }
  },

  /** Disconnect the linked X (Twitter) account, refresh and persist the profile, and show a confirmation notification. */
  async fetchDisconnectX({ commit }) {
    try {
      let response = await fetch(`/api/users/disconnect-x`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        let response = await fetch(`/api/users/${data.id}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        showNotification(
          "success",
          `X Account Disconnected`,
          "Your X account has been disconnected. Posts will no longer be published to X.",
          7000
        );
        commit("setUserData", profile);
        return data;
      } else {
        commit("setUserDataError", data);
        return data;
      }
    } catch (error) {
      // console.log("error", error);
      commit("setUserDataError", error.message);
    }
  },

  /** Set `state.authStatus` directly to `status`. */
  async addAuthStatus({ commit }, status) {
    commit("setAuthStatus", status);
  },
  /** Request an X (Twitter) OAuth request token, then redirect the browser to X's authorize page to start the link flow. */
  async authWithX({ commit }) {
    try {
      const response = await fetch(`/api/auth/login-x/request-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) {
        // console.log("error", data.message);
        commit("setUserDataError", data.message);
      }
      if (!data.statusCode) {
        document.location.href = `https://api.x.com/oauth/authenticate?oauth_token=${data.oauthToken}`;
      } else {
        commit("setUserDataError", data);
      }
    } catch (error) {}
  },
  /** Permanently delete the current user's account (with an optional `reason`), throwing if the API call fails. */
  async deleteCurrentUser({ commit }, { reason }) {
    try {
      const response = await fetch(`/api/users/me`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: reason ? JSON.stringify({ reason }) : undefined,
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("error:", errorData.message);
        throw new Error(errorData.message);
      }
      // console.log("User deletion successful");
    } catch (error) {
      // console.log("error", error);
      throw error;
    }
  },

  /** Reset `state.showSmsCodeInput` back to `false` (e.g. leaving/restarting the SMS-code entry step). */
  resetShowSmsCodeInput({ commit }) {
    commit("setShowSmsCodeInput", false);
  },

  /**
   * Handle the "Sign In With Farcaster" (SIWF) redirect callback for
   * `authorizationCode` `param`. On an invalidated-session 401/403, logs out
   * locally and routes to `/siwf/register`. On success: new users are stashed
   * in localStorage (`newUser`) and routed to `/siwf/register` to finish
   * signup; existing users have their profile loaded (backfilling default
   * `weighInThresholds` if missing), persisted, and committed, after which
   * global services (timer, reputation categories, unread notifications) are
   * kicked off before routing home.
   */
  async siwfCallback({ commit }, param) {
    try {
      let response = await fetch(
        `/api/siwf/callback?authorizationCode=${param}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 403 || response.status === 401) {
        const data = await response.json().catch(() => ({}));
        if (
          data?.message === "User account is deleted" ||
          data?.message === "User no longer exists" ||
          data?.message === "Unauthorized"
        ) {
          await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });

          localStorage.removeItem("user");
          localStorage.removeItem("activeTab");
          commit("setAuthStatus", false);
          router.push({
            path: "/siwf/register",
          });
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      let data = await response.json();

      // Check if the response indicates success
      if (response.status === 200 && (!data.statusCode || data.statusCode === 200)) {
        commit("setAuthStatus", true);

        if (data.is_new_user) {
          // Store all user data for new user registration
          localStorage.setItem("newUser", JSON.stringify(data));
          router.push({
            path: "/siwf/register",
          });
        } else {
          // Existing user flow
          try {
            let userResponse = await fetch(
              `/api/users/${data.user._id || data.user.id}`
            );

            let profile = await userResponse.json();
            if (userResponse.status === 404) {
              if (profile.message === "User not found") {
                showNotification(
                  "info",
                  `Info`,
                  `User not found. Please Signup`,
                  7000
                );
                router.push({
                  path: "/siwf/register",
                });
                return;
              }
            }

            // Ensure weighInThresholds exist
          if (!profile.weighInThresholds || !profile.weighInThresholds.length) {
              profile.weighInThresholds = [];
              let arr = [1, 2, 3, 4, 5];
              arr.forEach((item) => {
                profile.weighInThresholds.push({ threshold: "off" });
              });
            }

            localStorage.setItem("user", JSON.stringify(profile));
            commit("setUserData", profile);
            // Existing user completing SIWF auth — this is a login, not a
            // signup (the is_new_user branch above handles account creation).
            trackAnalyticsEvent("login", { method: "siwf", user_id_set: true, brand: analyticsBrand() });

            // Initialize app services after successful authentication
            try {
              // Start global services
            this.dispatch('startGlobalTimer');
            this.dispatch('fetchReputationCategories');
            this.dispatch('fetchUnreadNotificationsCount');
            // console.log('SIWF: Successfully initialized app services');
            } catch (serviceError) {
            console.warn('SIWF: Some services failed to initialize:', serviceError);
            }

            router.push("/");
          } catch (error) {
            console.error(error);
            throw new Error(`Authentication failed. Please try again.`);
          }
        }
      } else {
        // Handle error responses
        commit("setUserDataError", data);
        console.error('SIWF callback error:', data);
      }
    } catch (error) {
      console.error('SIWF callback failed:', error);
      commit("setUserDataError", { message: error.message });
      // Optionally redirect to an error page or show notification
      showNotification('error', 'Error', 'Authentication failed. Please try again.', 7000);

    }
  },
};
